<template>
  <div>
    <Steps :active="4" />

    <form @submit.prevent="send">
      <div class="flex flex-col justify-center content-center">
        <div class="font-bold text-2xl text-gray-700 mx-auto mb-4">How will you post your return?</div>

        <div class="flex flex-col mb-4 rounded bg-white max-w-screen-md mx-auto shadow-lg" v-if="largeParcelCount > 0">
          <div class="bg-red-700 w-100 p-4 text-white rounded-t">No drop off available</div>
          <div class="p-4">
            <div class="mb-4">At least one of your parcels is too big for a parcelshop and a collection is required.</div>

            <div>
              <strong>Please note:</strong>
              You will need access to a printer to print off the label for collection. If a label is not attached the
              parcel the courier can refuse collecting your parcel.
            </div>
          </div>
        </div>

        <div class="flex flex-col mb-4 rounded bg-white w-full max-w-screen-md mx-auto shadow-lg">
          <div class="p-4">
            <!-- HERMES DROP OFF - PRINT IN STORE - INSTRUCTIONS -->
            <!--
            <div>
              <div class="text-lg">
                <label class="font-bold">
                  <input
                    type="radio"
                    name="returnMethod"
                    v-model="returnMethod"
                    value="hermes-drop-off-print-in-store"
                    id="hermes-drop-off-print-in-store"
                  /> Hermes Drop Off
                </label>
                <div class="text-gray-900" v-if="returnMethod == 'hermes-drop-off-print-in-store'">
                  <small>You won't need a printer.</small>
                </div>
              </div>
              <dir class="p-0 text-gray-600" v-if="returnMethod == 'hermes-drop-off-print-in-store'">
                <label for="hermes-drop-off-print-in-store">
                  You will be presented with a reference number
                  <span
                    class="font-bold"
                    v-if="returns.length > 1"
                  >for each parcel</span> which you will use to
                  request a label at the Print-In Store machine in your local Hermes ParcelShop. To find the nearest
                  Hermes Drop off location where printing facilities are available, click
                  <a
                    :href="`https://www.hermesparcelreturn.co.uk/find-a-parcel-shop.html?search=${orderDetails.deliveryAddress.postcode}&printinstore=true`"
                    target="_blank"
                    class="font-bold"
                  >here</a>. Make sure you have the 'Print label at ParcelShop' selected when searching for the nearest
                  Hermes Print facilities.
                </label>

                <div class="mt-4 font-bold" v-if="returns.length > 1">
                  Keep in mind that you will need to repeat the process of printing a label at the parcelshop for
                  <strong>{{returns.length}}</strong> labels - one for each parcel.
                </div>
              </dir>
            </div>
            -->

            <!-- HERMES DROP OFF - PRINT IN STORE - QR CODE -->
            <div class="mb-4" v-if="largeParcelCount === 0">
              <div class="text-lg">
                <label
                  for="hermes-drop-off-qr-code"
                  class="font-bold border rounded p-6 border-gray-600 inline-block w-full cursor-pointer hover:bg-gray-200"
                  :class="{'bg-gray-400': returnMethod == 'hermes-drop-off-qr-code'}"
                >
                  <input
                    type="radio"
                    name="returnMethod"
                    v-model="returnMethod"
                    value="hermes-drop-off-qr-code"
                    id="hermes-drop-off-qr-code"
                  /> Hermes Drop Off - QR Code
                  <div
                    class="font-normal text-sm mt-4"
                  >Choose this option if you don't have a printer. You will be able to print the label at the parcelshop.</div>
                </label>
                <div class="text-gray-900 mt-2" v-if="returnMethod == 'hermes-drop-off-qr-code'">
                  <small>You won't need a printer.</small>
                </div>
              </div>
              <dir class="p-0 text-gray-600" v-if="returnMethod == 'hermes-drop-off-qr-code'">
                <label for="hermes-drop-off-qr-code">
                  Your QR code will be emailed to you which you can then scan at the Print-In Store machine in your local
                  ParcelShop. To find the nearest Hermes Drop off location where printing facilities are available, click
                  <a
                    :href="`https://www.hermesparcelreturn.co.uk/find-a-parcel-shop.html?search=${orderDetails.deliveryAddress.postcode}&printinstore=true`"
                    target="_blank"
                    class="text-gray-800 font-bold"
                  >here</a>. Make sure you have the 'Print label at ParcelShop' selected when searching for the nearest
                  Hermes Print facilities.
                </label>

                <div class="mt-4 font-bold" v-if="returns.length > 1">
                  Keep in mind that you will be presented with
                  <strong>{{returns.length}}</strong> QR codes - one for each parcel.
                </div>
              </dir>
            </div>

            <!-- HERMES DROP OFF - PRINT IN STORE - PRINT AT HOME -->
            <div class="mb-4" v-if="largeParcelCount === 0">
              <div class="text-lg">
                <label
                  for="hermes-drop-off-printer"
                  class="font-bold border rounded p-6 border-gray-600 inline-block w-full cursor-pointer hover:bg-gray-200"
                  :class="{'bg-gray-400': returnMethod == 'hermes-drop-off-printer'}"
                >
                  <input
                    type="radio"
                    name="returnMethod"
                    v-model="returnMethod"
                    value="hermes-drop-off-printer"
                    id="hermes-drop-off-printer"
                  /> Hermes Drop Off - Print Label at Home
                  <div class="font-normal text-sm mt-4">Choose this option if you have a printer.</div>
                </label>
                <div class="mt-2 text-gray-900" v-if="returnMethod == 'hermes-drop-off-printer'">
                  <small>You will need a printer.</small>
                </div>
              </div>
              <dir class="p-0 text-gray-700" v-if="returnMethod == 'hermes-drop-off-printer'">
                <label>
                  In the next step a label will be presented to you. We'll also email it to you. You'll need to print
                  the label, stick to the package and take to a parcelshop. You can find Hermes Parcel Shop stores near
                  you
                  <a
                    :href="`https://www.hermesparcelreturn.co.uk/find-a-parcel-shop.html?search=${orderDetails.deliveryAddress.postcode}&parcelshops=true`"
                    target="_blank"
                    class="font-bold"
                  >here</a>.
                </label>

                <div class="mt-4 font-bold" v-if="returns.length > 1">
                  Keep in mind that you will need to print
                  <strong>{{returns.length}}</strong> labels - one for each parcel.
                </div>
              </dir>
            </div>

            <!-- HERMES COLLECTION - PRINT AT HOME -->
            <div>
              <div class="text-lg">
                <label
                  for="hermes-collection-printer"
                  class="font-bold border rounded p-6 border-gray-600 inline-block w-full cursor-pointer hover:bg-gray-200"
                  :class="{'bg-gray-400': returnMethod == 'hermes-collection-printer'}"
                >
                  <input
                    type="radio"
                    name="returnMethod"
                    v-model="returnMethod"
                    value="hermes-collection-printer"
                    id="hermes-collection-printer"
                  /> Hermes Collection
                  <div class="font-normal text-sm mt-4">You must have a printer to print the return label.</div>
                </label>
                <div class="mt-2 text-gray-900" v-if="returnMethod == 'hermes-collection-printer'">
                  <small>You will need a printer.</small>
                </div>
              </div>
              <dir class="p-0 text-gray-700" v-if="returnMethod == 'hermes-collection-printer'">
                <label for="hermes-collection-printer">
                  A collection will be scheduled on the next business day (Mon - Fri: 8AM – 8PM, excluding Bank
                  Holidays). An adult must be present. Safety is always our top priority. To promote social distancing,
                  until further notice,
                  <u>delivery teams will no longer enter homes to pick up packages</u>.
                </label>

                <div class="mt-4 font-bold" v-if="returns.length > 1">
                  Keep in mind that you will need to print
                  <strong>{{returns.length}}</strong> labels - one for each parcel.
                </div>

                <div class="mt-4 mb-4 font-bold">Collection Address</div>

                <!-- FOR FUTURE USE -->
                <!-- <div class="flex justify-between mt-4">
                  <input
                    ref="postcode"
                    v-model="postcode"
                    class="border rounded p-4 text-center text-xl shadow-lg"
                    placeholder="Enter your postcode"
                    required="true"
                    tabindex="1"
                    autofocus
                  />
                  <ButtonSearch :class="{'bg-gray-700': postcode}" :disabled="postcode" @click="searchAddress" />
                </div>-->

                <div class="mt-6">
                  <div class="mt-4 font-bold">First Name *</div>
                  <div>
                    <input
                      type="text"
                      class="w-full border border-gray-500 rounded p-2 mt-2"
                      placeholder="Required"
                      :required="returnMethod == 'hermes-collection-printer'"
                      v-model="collectionAddress.first_name"
                    />
                  </div>
                  <div class="mt-4 font-bold">Last Name *</div>
                  <div>
                    <input
                      type="text"
                      class="w-full border border-gray-500 rounded p-2 mt-2"
                      placeholder="Required"
                      :required="returnMethod == 'hermes-collection-printer'"
                      v-model="collectionAddress.last_name"
                    />
                  </div>
                  <div class="mt-4 font-bold">Daytime Phone Number *</div>
                  <div>
                    <input
                      type="text"
                      class="w-full border border-gray-500 rounded p-2 mt-2"
                      placeholder="Required - Mobile is best"
                      :required="returnMethod == 'hermes-collection-printer'"
                      v-model="collectionAddress.telephone_number"
                    />
                  </div>
                  <div class="mt-4 font-bold">
                    1st line of address *
                    <small class="text-gray-500">(House Number/Name)</small>
                  </div>
                  <div>
                    <input
                      type="text"
                      class="w-full border border-gray-500 rounded p-2 mt-2"
                      placeholder="Required"
                      :required="returnMethod == 'hermes-collection-printer'"
                      v-model="collectionAddress.address_line_1"
                    />
                  </div>
                  <div class="mt-4 font-bold">
                    2nd line of address *
                    <small class="text-gray-500">(Street)</small>
                  </div>
                  <div>
                    <input
                      type="text"
                      class="w-full border border-gray-500 rounded p-2 mt-2"
                      placeholder="Required"
                      :required="returnMethod == 'hermes-collection-printer'"
                      v-model="collectionAddress.address_line_2"
                    />
                  </div>
                  <div class="mt-4 font-bold">
                    3rd line of address *
                    <small class="text-gray-500">(City)</small>
                  </div>
                  <div>
                    <input
                      type="text"
                      class="w-full border border-gray-500 rounded p-2 mt-2"
                      placeholder="Required"
                      :required="returnMethod == 'hermes-collection-printer'"
                      v-model="collectionAddress.address_line_3"
                    />
                  </div>
                  <div class="mt-4 font-bold">4th line of address</div>
                  <div>
                    <input
                      type="text"
                      class="w-full border border-gray-500 rounded p-2 mt-2"
                      v-model="collectionAddress.address_line_4"
                    />
                  </div>
                  <div class="mt-4 font-bold">
                    5th line of address
                    <small class="text-gray-500">(County)</small>
                  </div>
                  <div>
                    <input
                      type="text"
                      class="w-full border border-gray-500 rounded p-2 mt-2"
                      v-model="collectionAddress.address_line_5"
                    />
                  </div>
                  <div class="mt-4 font-bold">Postcode *</div>
                  <div>
                    <input
                      type="text"
                      class="w-full border border-gray-500 rounded p-2 mt-2 uppercase"
                      placeholder="Required"
                      :required="returnMethod == 'hermes-collection-printer'"
                      v-model="collectionAddress.postcode"
                    />
                  </div>
                </div>
              </dir>
            </div>
          </div>
        </div>
      </div>

      <div
        class="flex justify-center"
        v-if="returnMethod && (returnMethod != 'hermes-collection-printer' || returnMethod == 'hermes-collection-printer' && collectionAddress.valid)"
      >
        <ButtonNext class="bg-gray-700" />
      </div>
      <div
        class="flex justify-center m-auto text-sm bg-orange-500 text-white p-4 rounded max-w-md"
        v-else-if="returnMethod == 'hermes-collection-printer' && !collectionAddress.valid"
      >Please enter all required fields of your collection address.</div>
      <div
        class="flex justify-center m-auto text-sm bg-orange-500 text-white p-4 rounded max-w-md"
        v-else
      >Please choose a return method.</div>
    </form>
  </div>
</template>

<script>
import ButtonNext from '@/components/ButtonNext.vue'
import Steps from '@/components/Steps.vue'
import { mapActions, mapGetters } from 'vuex'
import api from '@/api'
export default {
  components: {
    ButtonNext,
    Steps,
  },
  data() {
    return {
      postcode: '',
      addressList: {},
      returnMethod: '',
      collectionAddress: {},
    }
  },
  computed: {
    ...mapGetters(['returns', 'orderDetails', 'largeParcelCount']),
  },
  watch: {
    collectionAddress() {
      this.collectionAddress.valid =
        this.collectionAddress.first_name &&
        this.collectionAddress.last_name &&
        this.collectionAddress.telephone_number &&
        this.collectionAddress.address_line_1 &&
        this.collectionAddress.address_line_2 &&
        this.collectionAddress.address_line_3 &&
        this.collectionAddress.postcode
    },
  },
  mounted() {},
  methods: {
    ...mapActions(['setReturnMethod', 'setCollectionAddress']),
    searchAddress() {
      api
        .post('/find-address', {
          postcode: this.postcode,
        })
        .then(res => {
          this.addressList = res.data
        })
        .catch(err => this.$snotify.error(err))
    },
    send() {
      this.setCollectionAddress(this.collectionAddress)
      this.setReturnMethod(this.returnMethod).then(() => this.$router.push('step5'))
    },
  },
}
</script>
